import type { ReactNode } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type { ProcessedFormField } from "@/types/api/formSchema";
import SubSectionHeading from "../components/SubSectionHeading";
import { AttachmentPreviewLink } from "../components/AttachmentPreviewLink";
import { formatValue } from "../utils/fieldFormat";
import {
  SECTION_ORDER,
  SECTION_TITLES,
  groupBranchFields,
} from "../utils/branchSections";
import type { PriorStep } from "./priorSteps";

export type AuthMode = "vendor" | "reviewer";

export function renderFieldValue(
  field: ProcessedFormField,
  raw: unknown,
  authMode: AuthMode,
): ReactNode | null {
  if (field.type === "attachment" && raw && typeof raw === "object") {
    const att = raw as { file_name?: string; attachment_id?: string };
    if (!att.attachment_id) return null;
    return (
      <AttachmentPreviewLink
        attachmentId={att.attachment_id}
        fileName={att.file_name}
        authMode={authMode}
      />
    );
  }
  return formatValue(field, raw);
}

function FieldsTable({
  fields,
  data,
  authMode,
}: {
  fields: ProcessedFormField[];
  data: Record<string, unknown>;
  authMode: AuthMode;
}) {
  const rows = fields
    .map((f) => ({ field: f, value: renderFieldValue(f, data[f.name], authMode) }))
    .filter((r) => r.value !== null);
  if (rows.length === 0) return null;
  return (
    <Table size="small" sx={{ "& td": { borderColor: "#E0E4EF" } }}>
      <TableBody>
        {rows.map(({ field, value }) => (
          <TableRow key={field.name}>
            <TableCell
              sx={{ width: "40%", color: "text.secondary", fontSize: 13 }}
            >
              {field.resolvedLabel}
            </TableCell>
            <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>
              {value}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function SectionedFields({
  fields,
  data,
  authMode,
}: {
  fields: ProcessedFormField[];
  data: Record<string, unknown>;
  authMode: AuthMode;
}) {
  const grouped = groupBranchFields(fields);
  return (
    <>
      {SECTION_ORDER.map((section) => {
        const sectionFields = grouped[section];
        if (!sectionFields || sectionFields.length === 0) return null;
        const rows = sectionFields
          .map((f) => ({
            field: f,
            value: renderFieldValue(f, data[f.name], authMode),
          }))
          .filter((r) => r.value !== null);
        if (rows.length === 0) return null;
        const title = SECTION_TITLES[section];
        return (
          <Box key={section} sx={{ mb: 1.5 }}>
            {title && (
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "text.secondary",
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  mt: 2,
                  mb: 0.75,
                }}
              >
                {title}
              </Typography>
            )}
            <FieldsTable fields={sectionFields} data={data} authMode={authMode} />
          </Box>
        );
      })}
    </>
  );
}

function StepSection({
  step,
  authMode,
}: {
  step: PriorStep;
  authMode: AuthMode;
}) {
  const stepName = step.metadata?.step_name;

  if (stepName === "business_details") {
    return (
      <Box sx={{ mb: 2 }}>
        <SubSectionHeading sx={{ borderTop: 0, pt: 0, mt: 0 }}>
          BUSINESS DETAILS
        </SubSectionHeading>
        <FieldsTable fields={step.fields} data={step.data} authMode={authMode} />
      </Box>
    );
  }

  if (stepName === "branches") {
    const arrayField = step.fields.find((f) => f.type === "array");
    const nested = (arrayField?.fields as ProcessedFormField[] | undefined) ?? [];
    const rawBranches = step.data.branches;
    const branches = Array.isArray(rawBranches)
      ? (rawBranches as Record<string, unknown>[])
      : [];
    if (branches.length === 0) return null;
    return (
      <>
        {branches.map((branch, idx) => (
          <Box key={idx} sx={{ mb: 3 }}>
            <Typography
              sx={{
                color: "primary.main",
                fontSize: 13,
                fontWeight: 700,
                mb: 1,
                mt: 3,
              }}
            >
              Branch #{idx + 1}
              {branch.is_primary_facility === true ? " ★ Primary" : ""}
            </Typography>
            <SectionedFields
              fields={nested}
              data={branch}
              authMode={authMode}
            />
          </Box>
        ))}
      </>
    );
  }

  // Unknown step_name — fall back to a plain fields table so vendors and
  // reviewers don't land on a blank Review page if BE adds a new step or
  // omits the step_name metadata.
  return (
    <Box sx={{ mb: 2 }}>
      <SubSectionHeading sx={{ borderTop: 0, pt: 0, mt: 0 }}>
        {(stepName ?? "step").replace(/_/g, " ").toUpperCase()}
      </SubSectionHeading>
      <FieldsTable fields={step.fields} data={step.data} authMode={authMode} />
    </Box>
  );
}

type Props = {
  priorSteps: PriorStep[];
  authMode: AuthMode;
};

export function VendorApplicationSummary({ priorSteps, authMode }: Props) {
  return (
    <>
      {priorSteps.map((step) => (
        <StepSection key={step.formId} step={step} authMode={authMode} />
      ))}
    </>
  );
}
